import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Wifi, Users, Smartphone, Monitor, AlertTriangle, CheckCircle, Info } from "lucide-react";

export const WifiPredictor = () => {
  const [attendees, setAttendees] = useState("100");
  const [usageProfile, setUsageProfile] = useState("moderate");
  const [devicesPerPerson, setDevicesPerPerson] = useState([1.5]);
  const [concurrentUsage, setConcurrentUsage] = useState([70]);

  // Bandwidth per device based on usage profile (in Mbps)
  const usageProfiles = {
    "minimal": {
      label: "Minimal (Email & Chat)",
      bandwidth: 0.5,
      description: "Basic email, messaging, and light web browsing",
      color: "text-green-400"
    },
    "light": {
      label: "Light (Web Browsing)",
      bandwidth: 1.5,
      description: "Web browsing, social media, document access",
      color: "text-cyan-400"
    },
    "moderate": {
      label: "Moderate (Video Calls)",
      bandwidth: 3,
      description: "Video conferencing, cloud apps, file sharing",
      color: "text-yellow-400"
    },
    "heavy": {
      label: "Heavy (HD Streaming)",
      bandwidth: 5,
      description: "HD video streaming, large downloads, live polling",
      color: "text-orange-400"
    },
    "extreme": {
      label: "Extreme (4K/Live Production)",
      bandwidth: 15,
      description: "4K streaming, live broadcasts, real-time collaboration",
      color: "text-red-400"
    }
  };

  // Calculate bandwidth requirements
  const numAttendees = parseInt(attendees) || 0;
  const devices = devicesPerPerson[0];
  const concurrent = concurrentUsage[0] / 100;
  const profile = usageProfiles[usageProfile];
  const bandwidthPerDevice = profile?.bandwidth || 3;

  const totalDevices = Math.ceil(numAttendees * devices);
  const activeDevices = Math.ceil(totalDevices * concurrent);
  const peakBandwidth = activeDevices * bandwidthPerDevice;
  const recommendedBandwidth = Math.ceil(peakBandwidth * 1.3); // 30% headroom
  const minimumBandwidth = Math.ceil(peakBandwidth * 0.8);

  // Determine connection type recommendation
  const getConnectionRecommendation = () => {
    if (recommendedBandwidth <= 50) {
      return { type: "Standard Business", color: "text-green-400", icon: CheckCircle };
    } else if (recommendedBandwidth <= 200) {
      return { type: "Business Fibre", color: "text-cyan-400", icon: CheckCircle };
    } else if (recommendedBandwidth <= 500) {
      return { type: "Dedicated Fibre", color: "text-yellow-400", icon: AlertTriangle };
    } else if (recommendedBandwidth <= 1000) {
      return { type: "Enterprise Fibre (1 Gbps)", color: "text-orange-400", icon: AlertTriangle };
    } else {
      return { type: "Multiple Dedicated Lines", color: "text-red-400", icon: AlertTriangle };
    }
  };

  const connectionRec = getConnectionRecommendation();
  const ConnectionIcon = connectionRec.icon;

  // Calculate access points needed (rule of thumb: 30-50 devices per AP)
  const accessPointsMin = Math.ceil(totalDevices / 50);
  const accessPointsMax = Math.ceil(totalDevices / 30);

  return (
    <Layout 
      title="WiFi Density Predictor" 
      subtitle="Calculate bandwidth requirements for your event"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Input Card */}
        <Card className="tactical-card border-cyan-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase text-cyan-400">
              <Users className="h-5 w-5" />
              Event Parameters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Attendees */}
              <div className="space-y-2">
                <Label className="label-tactical">Number of Attendees</Label>
                <Input
                  type="number"
                  min="1"
                  value={attendees}
                  onChange={(e) => setAttendees(e.target.value)}
                  className="tactical-input text-2xl font-mono h-14"
                  data-testid="attendees-input"
                />
              </div>

              {/* Usage Profile */}
              <div className="space-y-2">
                <Label className="label-tactical">Expected Usage</Label>
                <Select value={usageProfile} onValueChange={setUsageProfile}>
                  <SelectTrigger className="tactical-input h-14" data-testid="usage-profile-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(usageProfiles).map(([value, { label }]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{profile?.description}</p>
              </div>
            </div>

            {/* Sliders */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Devices per Person */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="label-tactical">Devices per Person</Label>
                  <span className="text-lg font-mono text-cyan-400">{devices.toFixed(1)}</span>
                </div>
                <Slider
                  value={devicesPerPerson}
                  onValueChange={setDevicesPerPerson}
                  min={1}
                  max={3}
                  step={0.1}
                  className="w-full"
                  data-testid="devices-slider"
                />
                <p className="text-xs text-muted-foreground">
                  Phone only: 1.0 | Phone + Laptop: 2.0 | Multiple devices: 2.5+
                </p>
              </div>

              {/* Concurrent Usage */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="label-tactical">Concurrent Usage</Label>
                  <span className="text-lg font-mono text-cyan-400">{concurrentUsage[0]}%</span>
                </div>
                <Slider
                  value={concurrentUsage}
                  onValueChange={setConcurrentUsage}
                  min={30}
                  max={100}
                  step={5}
                  className="w-full"
                  data-testid="concurrent-slider"
                />
                <p className="text-xs text-muted-foreground">
                  Breakout sessions: 50% | General session: 70% | Interactive: 90%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className="tactical-card border-cyan-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase text-cyan-400">
              <Wifi className="h-5 w-5" />
              Bandwidth Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-4 bg-secondary/30 rounded-sm">
                <Smartphone className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="label-tactical">Total Devices</p>
                <p className="text-3xl font-mono font-bold text-white" data-testid="total-devices">
                  {totalDevices}
                </p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-sm">
                <Monitor className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="label-tactical">Active at Peak</p>
                <p className="text-3xl font-mono font-bold text-yellow-400" data-testid="active-devices">
                  {activeDevices}
                </p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-sm">
                <Wifi className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="label-tactical">Peak Demand</p>
                <p className="text-3xl font-mono font-bold text-orange-400" data-testid="peak-bandwidth">
                  {peakBandwidth.toFixed(0)}
                </p>
                <p className="text-xs text-muted-foreground">Mbps</p>
              </div>
              <div className="p-4 bg-cyan-500/20 border border-cyan-500/30 rounded-sm">
                <Wifi className="h-5 w-5 mx-auto mb-2 text-cyan-400" />
                <p className="label-tactical">Recommended</p>
                <p className="text-3xl font-mono font-bold text-cyan-400" data-testid="recommended-bandwidth">
                  {recommendedBandwidth}
                </p>
                <p className="text-xs text-muted-foreground">Mbps</p>
              </div>
            </div>

            {/* Connection Recommendation */}
            <div className="p-4 bg-secondary/30 rounded-sm">
              <div className="flex items-center gap-3">
                <ConnectionIcon className={`h-6 w-6 ${connectionRec.color}`} />
                <div>
                  <p className="label-tactical">Recommended Connection Type</p>
                  <p className={`text-xl font-bold ${connectionRec.color}`}>{connectionRec.type}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Minimum: </span>
                  <span className="font-mono text-yellow-400">{minimumBandwidth} Mbps</span>
                </div>
                <div>
                  <span className="text-muted-foreground">With Headroom: </span>
                  <span className="font-mono text-cyan-400">{recommendedBandwidth} Mbps</span>
                </div>
              </div>
            </div>

            {/* Access Points */}
            <div className="p-4 bg-secondary/30 rounded-sm">
              <div className="flex items-center gap-3 mb-3">
                <Wifi className="h-5 w-5 text-purple-400" />
                <p className="label-tactical">Access Points Needed</p>
              </div>
              <p className="text-2xl font-mono font-bold text-purple-400">
                {accessPointsMin} - {accessPointsMax} APs
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Based on 30-50 devices per access point for optimal performance
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Venue Questions Card */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Info className="h-5 w-5" />
              Questions for the Venue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 font-bold">1.</span>
                <span>What is the total available bandwidth? <span className="text-cyan-400">(Need: {recommendedBandwidth} Mbps)</span></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 font-bold">2.</span>
                <span>Is this bandwidth dedicated or shared with other venue operations?</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 font-bold">3.</span>
                <span>How many access points cover the event space? <span className="text-cyan-400">(Need: {accessPointsMin}-{accessPointsMax} APs)</span></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 font-bold">4.</span>
                <span>Can we get a separate SSID/VLAN for event traffic?</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 font-bold">5.</span>
                <span>Is there a backup internet connection if the primary fails?</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 font-bold">6.</span>
                <span>Can we do a site survey to test actual coverage and speeds?</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Reference Guide */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="text-lg uppercase">Bandwidth Reference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              {Object.entries(usageProfiles).map(([key, { label, bandwidth, description, color }]) => (
                <div 
                  key={key}
                  className={`p-3 rounded-sm border ${usageProfile === key ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-secondary/30 border-border/50'}`}
                >
                  <p className={`font-semibold ${color}`}>{label}</p>
                  <p className="text-muted-foreground text-xs">{description}</p>
                  <p className="font-mono mt-1">{bandwidth} Mbps/device</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Reference Table */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="text-lg uppercase">Quick Reference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-muted-foreground">Attendees</th>
                    <th className="text-center py-2 text-muted-foreground">Light Use</th>
                    <th className="text-center py-2 text-muted-foreground">Moderate</th>
                    <th className="text-center py-2 text-muted-foreground">Heavy</th>
                  </tr>
                </thead>
                <tbody>
                  {[50, 100, 250, 500, 1000].map(n => {
                    const calc = (bw) => {
                      const dev = Math.ceil(n * 1.5);
                      const active = Math.ceil(dev * 0.7);
                      return Math.ceil(active * bw * 1.3);
                    };
                    return (
                      <tr key={n} className="border-b border-border/50">
                        <td className="py-2 font-mono">{n}</td>
                        <td className="py-2 text-center font-mono text-cyan-400">{calc(1.5)} Mbps</td>
                        <td className="py-2 text-center font-mono text-yellow-400">{calc(3)} Mbps</td>
                        <td className="py-2 text-center font-mono text-orange-400">{calc(5)} Mbps</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Based on 1.5 devices/person, 70% concurrent usage, 30% headroom
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default WifiPredictor;
